import { CalendarOptions, EventClickArg, EventDropArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg, EventResizeDoneArg } from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import { ResourceInput } from '@fullcalendar/resource';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import Popper from '@mui/material/Popper';
import { colorChannel } from '@mui/system/colorManipulator';
import React from 'react';
import { Box } from 'src/components/Box';
import { Card } from 'src/components/Card';
import { Grow } from 'src/components/transitions/Grow';

import { Dot } from '../Dot';
import { ListItem } from '../ListItem';
import { TitleAndClose } from '../TitleAndClose';
import { Theme, useMediaQuery, useTheme } from '../muiTheme/utils';

export type Mode = 'timeGridWeek' | 'resourceTimeGridDay' | 'listWeek' | 'dayGridMonth';
export type MorePopoverMode = 'custom' | 'native';

interface CalendarProps extends CalendarOptions {
  initialMode?: Mode;
  initialDate?: Date;
  resources?: ResourceInput[];
  events?: EventInput[];
  morePopoverMode?: MorePopoverMode;
  PopoverContent?: React.ComponentType<{ event: EventInput | null }>;
  listEventContent?: (event: EventInput) => React.ReactNode;
  ref: React.RefObject<FullCalendar | null>;
  onDateClick?: (info: DateClickArg) => void;
  onEventClick?: (info: EventClickArg) => void;
  // Drag and drop handlers
  onEventDrop?: (info: EventDropArg) => void;
  onEventResize?: (info: EventResizeDoneArg) => void;
  // Platform settings from v2
  firstDay?: number; // First day of week (0 = Sunday, 1 = Monday, etc.)
  scrollTime?: string; // Time to scroll to on load (e.g., '05:00:00')
  slotDuration?: string; // Time slot duration (e.g., '00:15:00' for 15 min, '00:30:00' for 30 min)
}

export const Calendar: React.FC<CalendarProps> = ({
  initialMode,
  resources,
  events,
  initialDate,
  morePopoverMode = 'custom',
  PopoverContent,
  listEventContent,
  onDateClick,
  onEventClick,
  onEventDrop,
  onEventResize,
  ref,
  // Platform settings
  firstDay = 0,
  scrollTime = '05:00:00',
  slotDuration = '00:30:00',
  ...otherProps
}) => {
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [hoveredEvent, setHoveredEvent] = React.useState<EventInput | null>(
    null
  );

  const [moreEventsAnchorEl, setMoreEventsAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const [moreEvents, setMoreEvents] = React.useState<EventInput[] | null>(null);

  const theme = useTheme();

  const popoverOpen = Boolean(anchorEl);
  const useNativeMorePopover = morePopoverMode === 'native';
  const handleCustomMoreLinkClick: NonNullable<CalendarOptions['moreLinkClick']> =
    (info) => {
      setMoreEventsAnchorEl(info.jsEvent.target as HTMLElement);
      setMoreEvents(info.hiddenSegs.map((seg) => seg.event as EventInput));
    };

  return (
    <Box
      sx={[
        { width: '100%' },
        ({ vars: { palette }, typography }) => ({
          '&&&& .fc': {
          '--fc-border-color': palette.divider,
          '--fc-today-bg-color': palette.primary['a2'],

          '& .fc-scrollgrid': {
            border: 'none',
          },
          '& .fc-scrollgrid-section > *': {
            border: 'none',
          },
          '& .fc-scrollgrid-section-header th': {
            border: 'none',
          },
          '& .fc-scroller-liquid-absolute': {
            border: `1px solid`,
            borderColor: palette.divider,
          },
          '& .fc-scrollgrid-section-sticky': {
            position: 'sticky',
            top: smDown ? '95px' : '193px',
            left: '0px',
            bgcolor: (theme) => theme.vars.palette.background.default,
            zIndex: 50,
          },
          '& .fc-scrollgrid-section-sticky th': {
            backgroundColor: 'transparent',
          },
          '& .fc-scrollgrid-section-body': {
            backgroundColor: palette.background.default,
          },

          '& .fc-timegrid-more-link': {
            boxShadow: 'none',
            border: `1px solid ${palette.background.default}`,
            backgroundColor: palette.secondary[4],
            overflow: 'hidden',
            fontSize: typography.caption.fontSize,
            py: 0,
          },
          '& .fc-timegrid-more-link-inner': {
            py: 0,
          },

          '& .fc-col-header-cell': {
            color: palette.text.secondary,
            fontSize: typography.body2.fontSize,
            fontWeight: typography.body2.fontWeight,
            border: 'none',
            textAlign: 'left',
            // textTransform: 'uppercase',
            pt: 0.5,
            pb: 0.5,

            '&.fc-day-today': {
              color: palette.text.primary,
            },
          },

          '& .fc-resourceTimeGridDay-view .fc-col-header-cell': {
            color: palette.text.primary,
          },

          '& .fc-resourceTimeGridDay-view': {
            '& .fc-day-today': {
              backgroundColor: 'transparent',
            },
          },
          ...(!useNativeMorePopover && {
            '& .fc-more-popover': {
              visibility: 'hidden',
            },
          }),

          '& .fc-event-main': {},
          '& .fc-event-time': {
            opacity: 0.6,
          },

          // Month view (dayGridMonth) event styling only
          '& .fc-dayGridMonth-view': {
            '& .fc-daygrid-event': {
              borderRadius: '4px',
              border: 'none',
              padding: '0px',
              fontSize: typography.caption.fontSize,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'transform 0.1s ease, box-shadow 0.1s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 2px 4px ${palette.action.focus}`,
              },
            },
            '& .fc-daygrid-event-dot': {
              display: 'none',
            },
            '& .fc-daygrid-dot-event': {
              padding: '2px 6px',
              '& .fc-event-title': {
                fontWeight: 500,
              },
            },
            '& .fc-daygrid-block-event .fc-event-main': {
              padding: '1px 2px',
            },
            '& .fc-daygrid-day-events': {
              marginTop: '2px',
            },
            '& .fc-daygrid-more-link': {
              fontSize: typography.caption.fontSize,
              fontWeight: 500,
              color: palette.primary.main,
              padding: '2px 6px',
              '&:hover': {
                backgroundColor: palette.action.hover,
                borderRadius: '4px',
              },
            },
          },

          // Make timegrid columns fill available width when there are few resources
          '& .fc-scrollgrid-sync-table': {
            minWidth: '100%',
          },
          '& .fc-timegrid-cols > table': {
            minWidth: '100%',
          },
          '& .fc-timegrid-col': {
            minWidth: '170px',
          },
          '& .fc-timegrid-event': {
            borderColor: `${palette.background.default}!important`,
            boxShadow: 'none',
            textShadow: `0px 0px 0px ${palette.text.primary}`,
          },

          '& .fc-timegrid-event .fc-event-main': {
            padding: '3px 3px',
          },
          '& .fc-timegrid-event .fc-event-time': {
            fontSize: '0.7em',
            lineHeight: 'normal',
          },
          '& .fc-timegrid-event .fc-event-title': {
            fontSize: '0.8em',
            lineHeight: 'normal',
          },
          '& .fc-timegrid-event-short': {
            // Compact styling for short events
            '& .fc-event-main': {
              padding: '1px 3px',
            },
            '& .fc-event-main-frame': {
              flexDirection: 'row',
              gap: '3px',
              alignItems: 'center',
            },
            '& .fc-event-time': {
              fontSize: '0.7em',
              flexShrink: 0,
              lineHeight: 'normal',
            },
            '& .fc-event-time::after': {
              display: 'none',
            },
            '& .fc-event-title': {
              fontSize: '0.8em',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          },
          '& .fc-timegrid-axis-cushion': {
            color: palette.text.secondary,
            fontSize: typography.caption.fontSize,
          },
          '& .fc-timegrid-slot-label-cushion': {
            color: palette.text.secondary,
            fontWeight: typography.caption.fontWeight,
            fontSize: typography.caption.fontSize,
            textTransform: 'uppercase',
            lineHeight: 1,
          },
          '& .fc-timegrid-slot:empty:after': {
            content: '"\u200B"',
          },

          '& .fc-list': {
            borderRadius: theme.vars.shape.borderRadius,
            overflow: 'hidden',
          },
          '& .fc-list-table .fc-list-event': {
            cursor: 'pointer',
            backgroundColor: palette.background.default + '!important',
          },
          '& .fc-list-event-time': {
            fontSize: {
              xs: String(typography.body2.fontSize),
              sm: String(typography.body1.fontSize),
            },
            maxWidth: {
              xs: '70px',
              sm: 'none',
            },
            textWrap: {
              xs: 'auto',
              sm: 'nowrap',
            },
          },
          '& .fc-list-table .fc-list-event:hover td': {
            backgroundColor: palette.background.default,
            fontSize: {
              xs: typography.body2.fontSize,
              sm: typography.body1.fontSize,
            },
          },
          '& .fc-list-day-cushion': {
            backgroundColor: palette.background.default,
            color: palette.text.secondary,
            fontWeight: typography.body1.fontWeight,
            fontSize: typography.body1.fontSize,
          },
        },
      }),
      ]}
    >
      {PopoverContent && (
        <Popper
          open={popoverOpen}
          anchorEl={anchorEl}
          sx={{ zIndex: 1500 }}
          placement="right-start"
          transition
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Card sx={{ p: 1 }}>
                <PopoverContent event={hoveredEvent} />
              </Card>
            </Grow>
          )}
        </Popper>
      )}

      {!useNativeMorePopover && (
        <Popper
          open={!!moreEventsAnchorEl}
          anchorEl={moreEventsAnchorEl}
          sx={{ zIndex: 1500 }}
          transition
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Card sx={{ minWidth: 250 }}>
                <TitleAndClose
                  title={`${moreEvents?.length} Events`}
                  onClose={() => setMoreEventsAnchorEl(null)}
                  titleVariant="h6"
                />
                <Box sx={{ mt: -2, mb: 1 }}>
                  {moreEvents?.map((event, index) => {
                    return (
                      <span
                        key={index}
                        onMouseEnter={(e) => {
                          setAnchorEl(e.target as HTMLElement);
                          setHoveredEvent(event as EventInput);
                        }}
                      >
                        <ListItem
                          avatar={<Dot sx={{ bgcolor: event.backgroundColor }} />}
                          primary={event.title}
                          clickable={!!onEventClick}
                          onClick={(e) => {
                            if (!onEventClick || !e) return;
                            setMoreEventsAnchorEl(null);
                            setAnchorEl(null);
                            onEventClick({
                              el: e.currentTarget as HTMLElement,
                              event: event,
                              jsEvent: e.nativeEvent as MouseEvent,
                              view: ref.current?.getApi().view,
                            } as EventClickArg);
                          }}
                        />
                      </span>
                    );
                  })}
                </Box>
              </Card>
            </Grow>
          )}
        </Popper>
      )}

      <FullCalendar
        ref={ref}
        plugins={[
          dayGridPlugin,
          timeGridPlugin,
          scrollGridPlugin,
          resourceTimeGridPlugin,
          interactionPlugin,
          listPlugin,
        ]}
        schedulerLicenseKey={import.meta.env.VITE_FULLCALENDAR_LICENSE_KEY}
        initialDate={initialDate}
        initialView={initialMode}
        weekends={true}
        headerToolbar={otherProps.headerToolbar ?? false}
        allDaySlot={false}
        height="auto"
        resources={resources}
        events={events}
        defaultRangeSeparator={' - '}
        dayMinWidth={170}
        stickyHeaderDates={true}
        stickyFooterScrollbar={true}
        firstDay={firstDay}
        scrollTime={scrollTime}
        slotDuration={slotDuration}
        resourceOrder="sortOrder"
        eventOrder="sortOrder,start,title"
        eventMouseEnter={(info) => {
          if (info.jsEvent.target) {
            setAnchorEl(info.jsEvent.target as HTMLElement);
            setHoveredEvent(info.event as EventInput);
          }
        }}
        eventDidMount={(e) => {
          const baseBgColor = theme.vars.palette.background.level1;
          const bg1 = theme.vars.palette.background.level2;
          const eventColor = e.event.extendedProps.data?.color;

          if (eventColor) {
            const fadedEventColor = `rgba(${colorChannel(eventColor)} / 0.2)`;
            e.el.style.background = `linear-gradient(0deg, ${fadedEventColor}, ${fadedEventColor}), linear-gradient(90deg, ${baseBgColor}, ${bg1})`;
          }

          if (e.view.type === 'listWeek') {
            e.el.style.background = theme.vars.palette.background.level1;
          }

          // Month view specific styling (applied last to override gradient)
          if (e.view.type === 'dayGridMonth') {;
            const monthColor = e.event.backgroundColor || eventColor;
            if (monthColor && monthColor !== 'transparent') {
              const fadedColor = `rgba(${colorChannel(monthColor)} / 0.15)`;
              e.el.style.backgroundColor = fadedColor;
              e.el.style.borderLeft = `3px solid ${monthColor}`;
              e.el.style.color = theme.vars.palette.text.primary;
            } else {
              e.el.style.backgroundColor = theme.vars.palette.action.hover;
              e.el.style.borderLeft = `3px solid ${theme.vars.palette.primary.main}`;
            }
            e.el.style.background = ''; // Clear the gradient
          }

          window.setTimeout(() => {
            const elemIsInset =
              (e.el.parentNode as HTMLElement).style.left !== '0%';
            if (elemIsInset) {
              e.el.classList.add('fc-timegrid-event-inset');
            }
          }, 0);
        }}
        eventContent={(e) => {
          if (e.view.type !== 'listWeek') {
            return (
              <div className="fc-event-main-frame">
                <div className="fc-event-time">{e.timeText}</div>
                <div className="fc-event-title-container">
                  <div className="fc-event-title fc-sticky">
                    {e.event.title}
                  </div>
                </div>
              </div>
            );
          }
          if (e.view.type === 'listWeek' && listEventContent) {
            return listEventContent(e);
          }
        }}
        listDayFormat={{
          month: 'short',
          year: 'numeric',
          day: 'numeric',
          weekday: 'long',
        }}
        moreLinkClick={
          useNativeMorePopover ? 'popover' : handleCustomMoreLinkClick
        }
        listDaySideFormat={false}
        eventMaxStack={smDown ? 2 : 3}
        slotEventOverlap={!smDown}
        dateClick={onDateClick}
        eventClick={onEventClick}
        eventDrop={onEventDrop}
        eventResize={onEventResize}
        eventMouseLeave={() => {
          setAnchorEl(null);
        }}
        {...otherProps}
      />
    </Box>
  );
};
